/**
 * IntegrationsHubPage – Centrum Integracji.
 * Shows ONLY actually implemented integrations with live connection status.
 * Organized by use-case (E-Commerce, Call Funnels, SaaS).
 */
import { useState, useEffect, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest, getAuthToken } from "@/lib/queryClient";
import { escapeHtml } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useStore } from "@/lib/store";
import { resolveCapiClientScope } from "@/lib/capi-client-scope";
import { CONNECT_IGNORES_CLIENT_SCOPE, scopeForIntegration } from "@/lib/integration-account-scope";
import { ORGANIC_CONTEXT_PARAM, resolveIntegrationScope } from "@/lib/view-mode";
import CapiConfigPage from "@/components/CapiConfig";
import AnalyticsSettingsModal from "@/components/analytics-settings-modal";
import { SlidersHorizontal } from "lucide-react";
import { useAgencyClients } from "@/hooks/useAgencyClients";
import TrackingConnectPanel from "@/components/tracking-connect-panel";
import { ClientApiPanel } from "@/components/client-api-panel";
import { McpKeyPanel } from "@/components/mcp-key-panel";
import CrmWebhookSetupPanel from "@/components/crm-webhook-setup-panel";
import StripeTrackingSetupPanel from "@/components/stripe-tracking-setup-panel";
import EverwebinarConfigPanel from "@/components/everwebinar-config-panel";
import ShopifyConnectPanel from "@/components/shopify-connect-panel";
import BaseLinkerConnectPanel from "@/components/baselinker-connect-panel";
import RingostatConnectPanel from "@/components/ringostat-connect-panel";
import HubspotStageMappingPanel from "@/components/hubspot-stage-mapping-panel";
import { GhlSubaccountsPanel } from "@/components/ghl-subaccounts-panel";
import { GhlCalendarSelectionPanel } from "@/components/ghl-calendar-selection-panel";
import { GhlTagsGuidePanel } from "@/components/ghl-tags-guide-panel";
import { GhlWebhookSetupPanel } from "@/components/ghl-webhook-setup-panel";
import MetaConversionMappingPanel from "@/components/meta-conversion-mapping-panel";
import {
  SlackNotificationsPanel,
  type AgencyClientOption,
} from "@/components/slack-notifications-panel";
import { useAuth } from "@/hooks/useAuth";
import { canAccessShopifyIntegration } from "@shared/shopify-access";
import { canAccessGoogleAdsIntegration } from "@shared/google-ads-access";
import { PaywallModal } from "@/components/paywall-modal";
import { Skeleton } from "@/components/ui/skeleton";
import { KpiGridSkeleton, TableSkeleton } from "@/components/skeletons";
import {
  ArrowLeft,
  Check,
  Loader2,
  Search,
  Megaphone,
  ShoppingCart,
  Phone,
  Crosshair,
  Webhook,
  FileSpreadsheet,
  Bell,
  Plug,
  Unplug,
  ChevronDown,
  ChevronUp,
  MonitorSmartphone,
  Layers,
  Send,
  Lock,
  ExternalLink,
  Zap,
  Users,
} from "lucide-react";

// ═══════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════

type UseCase = "all" | "ecommerce" | "call_funnel" | "saas";
type ConnectionType = "oauth" | "api_key" | "webhook" | "script" | "config" | "url";

/**
 * VP-116: do którego planu należy integracja.
 *  - `organic`      — sens ma tylko przy tworzeniu treści,
 *  - `performance`  — reklamy, analityka, CRM/e-commerce,
 *  - `both`         — potrzebne w obu (np. Meta: jedno OAuth obsługuje i publikację,
 *                     i reklamy — patrz ADR-0006 D2).
 *
 * Pole jest WYMAGANE celowo: nowa integracja bez świadomej decyzji ma nie kompilować się,
 * zamiast po cichu wejść jako „dostępna dla wszystkich".
 */
type IntegrationPlan = "organic" | "performance" | "both";

type Integration = {
  id: string;
  name: string;
  icon: string;
  desc: string;
  type: ConnectionType;
  typeLabel: string;
  useCases: UseCase[];
  category: string;
  plan: IntegrationPlan;
  helpText?: string;
};

// ═══════════════════════════════════════════════════════
// All REAL integrations (only what's actually implemented)
// ═══════════════════════════════════════════════════════

const INTEGRATIONS: Integration[] = [
  // ─── Ad Platforms ───
  {
    id: "meta",
    name: "Meta Ads (Facebook + Instagram)",
    icon: "📘",
    desc: "integrations.meta.desc",
    type: "oauth",
    typeLabel: "OAuth 2.0",
    useCases: ["ecommerce", "call_funnel", "saas"],
    category: "ads",
    plan: "both",
    helpText: "integrations.meta.helpText",
  },
  {
    id: "google_ads",
    name: "Google Ads",
    icon: "🔴",
    desc: "integrations.google_ads.desc",
    type: "oauth",
    typeLabel: "OAuth 2.0",
    useCases: ["ecommerce", "call_funnel", "saas"],
    category: "ads",
    plan: "performance",
    helpText: "integrations.google_ads.helpText",
  },
  {
    id: "tiktok",
    name: "TikTok (Content Publish)",
    icon: "🎵",
    desc: "integrations.tiktok.desc",
    type: "oauth",
    typeLabel: "OAuth 2.0",
    useCases: ["ecommerce", "call_funnel", "saas"],
    category: "ads",
    plan: "organic",
    helpText: "integrations.tiktok.helpText",
  },
  {
    id: "tiktok_ads",
    name: "TikTok Ads (Lead Gen)",
    icon: "🎯",
    desc: "Pobieranie leadów z formularzy błyskawicznych TikTok Lead Gen do CRM",
    type: "oauth",
    typeLabel: "OAuth 2.0",
    useCases: ["call_funnel", "saas", "ecommerce"],
    category: "ads",
    plan: "performance",
    helpText:
      "Połącz konto TikTok for Business, wybierz konto reklamowe i synchronizuj leady z formularzy błyskawicznych (Instant Forms) do swojego CRM.",
  },
  // YouTube + LinkedIn (Content Publish) widoczne tylko dla super_admin (gating w komponencie)
  {
    id: "youtube",
    name: "YouTube (Content Publish)",
    icon: "▶️",
    desc: "Publikacja filmów i Shortów ze Schedulera bezpośrednio na kanał YouTube",
    type: "oauth",
    typeLabel: "OAuth 2.0",
    useCases: ["ecommerce", "call_funnel", "saas"],
    category: "ads",
    plan: "organic",
    helpText:
      "Połącz konto Google z dostępem do kanału YouTube, aby publikować zaplanowane filmy i Shorty z modułu Scheduler.",
  },
  {
    id: "linkedin",
    name: "LinkedIn (Content Publish)",
    icon: "🔗",
    desc: "Publikacja postów z Schedulera na profil osobisty albo stronę firmową LinkedIn",
    type: "oauth",
    typeLabel: "OAuth 2.0",
    useCases: ["ecommerce", "call_funnel", "saas"],
    category: "ads",
    plan: "organic",
    helpText:
      "Połącz konto LinkedIn, a potem wybierz czy Scheduler ma publikować jako profil osobisty czy strona firmowa.",
  },
  {
    id: "allegro",
    name: "Allegro + Allegro Ads",
    icon: "🟠",
    desc: "integrations.allegro.desc",
    type: "oauth",
    typeLabel: "OAuth 2.0",
    useCases: ["ecommerce"],
    category: "ads",
    plan: "performance",
    helpText: "integrations.allegro.helpText",
  },

  // ─── E-Commerce / Marketplace ───
  {
    id: "shopify",
    name: "Shopify",
    icon: "🛒",
    desc: "integrations.shopify.desc",
    type: "api_key",
    typeLabel: "OAuth (App Store)",
    useCases: ["ecommerce"],
    category: "ecommerce",
    plan: "performance",
  },
  {
    id: "baselinker",
    name: "BaseLinker",
    icon: "📦",
    desc: "integrations.baselinker.desc",
    type: "api_key",
    typeLabel: "API Token",
    useCases: ["ecommerce"],
    category: "ecommerce",
    plan: "performance",
    helpText: "integrations.baselinker.helpText",
  },
  {
    id: "gmc",
    name: "Google Merchant Center",
    icon: "🛍️",
    desc: "integrations.gmc.desc",
    type: "oauth",
    typeLabel: "OAuth 2.0",
    useCases: ["ecommerce"],
    category: "ecommerce",
    plan: "performance",
    helpText: "integrations.gmc.helpText",
  },

  // ─── CRM & Telefonia ───
  {
    id: "ghl",
    name: "LeadConnector (CRM)",
    icon: "🟢",
    desc: "integrations.ghl.desc",
    type: "oauth",
    typeLabel: "OAuth 2.0",
    useCases: ["call_funnel", "saas"],
    category: "crm",
    plan: "performance",
    helpText: "integrations.ghl.helpText",
  },
  {
    id: "hubspot",
    name: "HubSpot (CRM)",
    icon: "🟠",
    desc: "integrations.hubspot.desc",
    type: "oauth",
    typeLabel: "OAuth 2.0",
    useCases: ["call_funnel", "saas", "ecommerce"],
    category: "crm",
    plan: "performance",
    helpText: "integrations.hubspot.helpText",
  },
  {
    id: "ringostat",
    name: "Ringostat (Telefonia / PBX)",
    icon: "📞",
    desc: "integrations.ringostat.desc",
    type: "api_key",
    typeLabel: "Webhook (bez API Key)",
    useCases: ["call_funnel"],
    category: "crm",
    plan: "performance",
  },
  {
    id: "calendly",
    name: "Calendly",
    icon: "📅",
    desc: "integrations.calendly.desc",
    type: "oauth",
    typeLabel: "OAuth 2.0",
    useCases: ["call_funnel", "saas"],
    category: "crm",
    plan: "performance",
    helpText: "integrations.calendly.helpText",
  },
  {
    id: "everwebinar",
    name: "EverWebinar",
    icon: "🎬",
    desc: "integrations.everwebinar.desc",
    type: "api_key",
    typeLabel: "API Key",
    useCases: ["call_funnel"],
    category: "crm",
    plan: "performance",
    helpText: "integrations.everwebinar.helpText",
  },

  // ─── Tracking ───
  {
    id: "tracking",
    name: "Tracking Script (Pixel)",
    icon: "🎯",
    desc: "integrations.tracking.desc",
    type: "script",
    typeLabel: "Snippet HTML",
    useCases: ["ecommerce", "call_funnel", "saas"],
    category: "tracking",
    plan: "performance",
    helpText: "integrations.tracking.helpText",
  },
  {
    id: "capi",
    name: "Server-Side Tracking (CAPI)",
    icon: "🚀",
    desc: "integrations.capi.desc",
    type: "config",
    typeLabel: "Conversions API",
    useCases: ["ecommerce", "call_funnel", "saas"],
    category: "tracking",
    plan: "performance",
    helpText: "integrations.capi.helpText",
  },

  // ─── Automatyzacje ───
  {
    id: "n8n_ideas",
    name: "n8n (Idea Bank)",
    icon: "🔗",
    desc: "integrations.n8n_ideas.desc",
    type: "webhook",
    typeLabel: "Webhook",
    useCases: ["saas"],
    category: "automation",
    plan: "organic",
    helpText: "integrations.n8n_ideas.helpText",
  },

  // ─── Notifications ───
  {
    id: "slack",
    name: "Slack (Notifications)",
    icon: "💬",
    desc: "integrations.slack.desc",
    type: "oauth",
    typeLabel: "OAuth 2.0",
    useCases: ["ecommerce", "call_funnel", "saas"],
    category: "notifications",
    plan: "performance",
    helpText: "integrations.slack.helpText",
  },

  // ─── External API ───
  {
    id: "external_api",
    name: "External API",
    icon: "🔑",
    desc: "integrations.external_api.desc",
    type: "api_key",
    typeLabel: "API Key",
    useCases: ["saas", "ecommerce", "call_funnel"],
    category: "crm",
    plan: "performance",
    helpText: "integrations.external_api.helpText",
  },

  // ─── Claude MCP ───
  {
    id: "claude_mcp",
    name: "Claude MCP (Scheduler)",
    icon: "🤖",
    desc: "integrations.claude_mcp.desc",
    type: "api_key",
    typeLabel: "API Key (MCP)",
    useCases: ["saas", "ecommerce", "call_funnel"],
    category: "automation",
    // Klucze API żyją za requirePerformancePlan (mount /api/external/keys) — karta
    // musi być spójna z backendem, inaczej Organic dostaje martwy formularz.
    plan: "performance",
    helpText: "integrations.claude_mcp.helpText",
  },

  // ─── Stripe → sprzedaże/zwroty (VP-261: per-klient URL + weryfikacja podpisu) ───
  {
    id: "stripe",
    name: "Stripe",
    icon: "💳",
    desc: "integrations.stripe.desc",
    type: "webhook",
    typeLabel: "Webhook (podpisany)",
    useCases: ["saas", "ecommerce"],
    category: "ecommerce",
    plan: "performance",
    helpText: "integrations.stripe.helpText",
  },

  // ─── Universal CRM Webhook ───
  {
    id: "crm_webhook",
    name: "Any CRM → Webhook",
    icon: "🔗",
    desc: "integrations.crm_webhook.desc",
    type: "webhook",
    typeLabel: "Webhook + API Key",
    useCases: ["call_funnel", "saas", "ecommerce"],
    category: "crm",
    plan: "performance",
    helpText: "integrations.crm_webhook.helpText",
  },
];

// ═══════════════════════════════════════════════════════
// Categories & Use Cases
// ═══════════════════════════════════════════════════════

// Strzałka „wstecz" w nagłówku huba — dashboard w shellu czatu (z sidebarem).
const DASHBOARD_PATH = "/chat/dashboard";

const CATEGORIES = [
  { id: "ads", label: "categories.ads", icon: Megaphone, color: "blue" },
  { id: "ecommerce", label: "categories.ecommerce", icon: ShoppingCart, color: "emerald" },
  { id: "crm", label: "categories.crm", icon: Phone, color: "purple" },
  { id: "tracking", label: "categories.tracking", icon: Crosshair, color: "red" },
  { id: "data", label: "categories.data", icon: FileSpreadsheet, color: "cyan" },
  { id: "automation", label: "categories.automation", icon: Webhook, color: "orange" },
  { id: "notifications", label: "categories.notifications", icon: Bell, color: "pink" },
];

const USE_CASES: { id: UseCase; label: string; desc: string; icon: React.ElementType }[] = [
  { id: "all", label: "useCases.all.label", desc: "useCases.all.desc", icon: Layers },
  {
    id: "call_funnel",
    label: "useCases.callFunnel.label",
    desc: "useCases.callFunnel.desc",
    icon: Phone,
  },
  {
    id: "ecommerce",
    label: "useCases.ecommerce.label",
    desc: "useCases.ecommerce.desc",
    icon: ShoppingCart,
  },
  { id: "saas", label: "useCases.saas.label", desc: "useCases.saas.desc", icon: MonitorSmartphone },
];

/**
 * VP-300: przełącznik zakresu huba dla usera, który ma i Organica, i Performance.
 * Wejście z Organica (Analityka → „Przejdź do integracji") pokazuje wyłącznie publikację
 * i analitykę treści; reklamy, CRM i e-commerce chowają się za drugą zakładką.
 */
const SCOPE_TABS: { id: string; label: string; showPerformance: boolean }[] = [
  { id: "organic", label: "scope.organic", showPerformance: false },
  { id: "ads", label: "scope.ads", showPerformance: true },
];

const COLOR_MAP: Record<string, { bg: string; text: string; border: string }> = {
  red: { bg: "bg-red-500/10", text: "text-red-400", border: "border-red-500/20" },
  blue: { bg: "bg-blue-500/10", text: "text-blue-400", border: "border-blue-500/20" },
  amber: { bg: "bg-amber-500/10", text: "text-amber-400", border: "border-amber-500/20" },
  purple: { bg: "bg-purple-500/10", text: "text-purple-400", border: "border-purple-500/20" },
  emerald: { bg: "bg-emerald-500/10", text: "text-emerald-400", border: "border-emerald-500/20" },
  cyan: { bg: "bg-cyan-500/10", text: "text-cyan-400", border: "border-cyan-500/20" },
  orange: { bg: "bg-orange-500/10", text: "text-orange-400", border: "border-orange-500/20" },
  pink: { bg: "bg-pink-500/10", text: "text-pink-400", border: "border-pink-500/20" },
};

// ═══════════════════════════════════════════════════════
// Plan-based locking
// ═══════════════════════════════════════════════════════

// Map plan vertical → default pre-selected filter when user enters the hub.
const PLAN_DEFAULT_FILTER: Record<string, UseCase> = {
  lead_generation: "call_funnel",
  ecommerce: "ecommerce",
  content_creation: "all",
};

const PLAN_TIER_PRIORITY: Record<string, number> = {
  start: 1,
  pro: 2,
  expert: 3,
};

const STARTER_LOCKED_INTEGRATIONS = new Set<string>([
  "ghl",
  "hubspot",
  "ringostat",
  "calendly",
  "everwebinar",
  "external_api",
  "crm_webhook",
  "zapier",
  "ghl_webhook",
  "n8n_ideas",
  "stripe",
]);

function getPlanKeys(planKey: string | null | undefined): string[] {
  if (!planKey) return [];
  return planKey
    .split(",")
    .map((key) => key.trim())
    .filter((key) => key.length > 0);
}

/**
 * VP-116: domyślna zakładka filtra. Czyta WSZYSTKIE plany i wybiera pierwszy vertical,
 * dla którego mamy zdefiniowany filtr — wcześniej brało `planKeys[0]`, więc user
 * z Performance + e-commerce dostawał inną zakładkę zależnie od kolejności planów
 * w stringu. To ta sama klasa buga, którą naprawia plan-vertical.ts.
 */
function getDefaultFilterVertical(planKey: string | null | undefined): string | null {
  const verticals = getPlanKeys(planKey)
    .map((key) => key.split(":")[0])
    .filter((vertical): vertical is string => !!vertical);
  return verticals.find((vertical) => vertical in PLAN_DEFAULT_FILTER) ?? null;
}

function getPlanTier(planKey: string | null | undefined): string | null {
  const tiers = getPlanKeys(planKey)
    .map((key) => key.split(":")[1])
    .filter((tier): tier is string => !!tier && tier in PLAN_TIER_PRIORITY);

  if (tiers.length === 0) return null;

  return tiers.reduce((bestTier, currentTier) =>
    PLAN_TIER_PRIORITY[currentTier] > PLAN_TIER_PRIORITY[bestTier] ? currentTier : bestTier
  );
}

type LockReason = "tier" | "vertical";

interface LockState {
  locked: boolean;
  reason: LockReason | null;
}

/**
 * VP-116: blokada wynika z JAWNEGO pola `plan` karty, nie z pokrycia `useCases`.
 * Poprzednia wersja zwracała `locked` dla KAŻDEJ karty przy planie Organic
 * (`PLAN_USE_CASES.content_creation` to pusta tablica) — czyli blokowała też Meta,
 * bez której Organic nie opublikuje posta ani nie odpali Automatyzacji.
 */
function resolveLockState(
  integration: Integration,
  hasPerformance: boolean,
  planTier: string | null
): LockState {
  if (planTier === "start" && STARTER_LOCKED_INTEGRATIONS.has(integration.id)) {
    return { locked: true, reason: "tier" };
  }
  if (integration.plan === "performance" && !hasPerformance) {
    return { locked: true, reason: "vertical" };
  }
  return { locked: false, reason: null };
}

// Backward-compat boolean wrapper (still used by list renderers for `locked` flag).
function isIntegrationLocked(
  integration: Integration,
  hasPerformance: boolean,
  planTier: string | null
): boolean {
  return resolveLockState(integration, hasPerformance, planTier).locked;
}

// ═══════════════════════════════════════════════════════
// Live status hook
// ═══════════════════════════════════════════════════════

// Per-platform usage cap for the current user. Used to disable "Connect"
// buttons when `used >= limit` so a `start`-plan user can't accidentally
// race a 2nd connect past the backend swap-replace.
type PlatformUsage = { used: number; limit: number | null };
type PlanUsageResponse = { usage: Record<string, PlatformUsage> };

// IntegrationCard.id → backend SupportedPlatform key.
// Mismatches (e.g. `gmc` UI id vs `google_merchant` backend key) live here.
const INTEGRATION_TO_PLATFORM: Record<string, string> = {
  meta: "meta",
  google_ads: "google_ads",
  tiktok: "tiktok",
  tiktok_ads: "tiktok_ads",
  youtube: "youtube",
  linkedin: "linkedin",
  ghl: "ghl",
  hubspot: "hubspot",
  calendly: "calendly",
  allegro: "allegro",
  gmc: "google_merchant",
  baselinker: "baselinker",
  shopify: "shopify",
  everwebinar: "everwebinar",
  google_drive: "cloud_storage",
  ringostat: "ringostat",
  slack: "slack",
};

function usePlanUsage() {
  return useQuery<PlanUsageResponse>({
    queryKey: ["plan-usage"],
    queryFn: () => apiRequest("GET", "/api/plan/usage"),
    staleTime: 30_000,
    retry: false,
  });
}

/**
 * Wspolny ksztalt odpowiedzi /api/integrations/<x>/status. Kazda integracja dokłada
 * wlasne pola (np. channelTitle dla YouTube, webinar_name dla EverWebinar), stad
 * sygnatura indeksowa — helper `set()` czyta tylko czesc wspolna, a per-integracyjne
 * pola sa siegane w callbackach check/detail.
 */
type IntegrationStatusResponse = {
  connected?: boolean;
  configured?: boolean;
  accountName?: string;
  shopDomain?: string;
  projectName?: string;
  selectedAccount?: string;
  selectedAdvertiserName?: string;
  businessName?: string;
  channelTitle?: string;
  googleEmail?: string;
  calendlyUserName?: string;
  calendlyUserEmail?: string;
  webinar_name?: string;
  linkedinName?: string;
  linkedinOrganizationName?: string;
  linkedinPublishAs?: string;
  linkedinOrganizationId?: string | null;
  scopes?: string;
  integrations?: Array<{ label?: string; teamName?: string }>;
  events?: number;
  visitors?: number;
  total?: number;
  sent?: number;
  failed?: number;
  lastEventType?: string | null;
  keys?: Array<{ id: string; revoked_at?: string | null; scopes?: string[] }>;
  google_drive?: { connected?: boolean; email?: string };
  [field: string]: unknown;
};

function useIntegrationStatuses(
  forUserId?: string | null,
  canSeeShopify: boolean = true,
  canSeeGoogleAds: boolean = true,
  showPerformance: boolean = true
) {
  type ShopifyStatusResponse = {
    connected: boolean;
    shopDomain?: string;
    shopName?: string;
    appStoreListingUrl?: string | null;
    canSelfServeInstall: boolean;
    needsReauth?: boolean;
  };
  const { t: statusT } = useTranslation();
  // Append ?forUserId=<clientUserId> for agency users browsing a client's
  // integrations. Backend endpoints check this param and proxy the lookup
  // through the agency-membership chain.
  const q = (suffix: string) =>
    forUserId ? `${suffix}${suffix.includes("?") ? "&" : "?"}forUserId=${forUserId}` : suffix;
  const k = (key: string) => (forUserId ? [key, forUserId] : [key]);
  const meta = useQuery<IntegrationStatusResponse>({
    queryKey: k("hub-meta"),
    queryFn: () => apiRequest("GET", q("/api/integrations/meta/status")),
    staleTime: 60_000,
    retry: false,
  });
  const gads = useQuery<IntegrationStatusResponse>({
    queryKey: k("hub-gads"),
    queryFn: () => apiRequest("GET", q("/api/integrations/google-ads/status")),
    staleTime: 60_000,
    retry: false,
    // VP-192: karta ukryta dla zwykłych userów, więc nie ma po co pytać o status.
    enabled: canSeeGoogleAds && showPerformance,
  });
  const tiktok = useQuery<IntegrationStatusResponse>({
    queryKey: k("hub-tiktok"),
    queryFn: () => apiRequest("GET", q("/api/integrations/tiktok/status")),
    staleTime: 60_000,
    retry: false,
  });
  const tiktokAds = useQuery<IntegrationStatusResponse>({
    queryKey: k("hub-tiktok_ads"),
    queryFn: () => apiRequest("GET", q("/api/integrations/tiktok-ads/status")),
    staleTime: 60_000,
    retry: false,
    // VP-116: integracja Performance — dla Organica endpoint zwraca 403.
    enabled: showPerformance,
  });
  const youtube = useQuery<IntegrationStatusResponse>({
    queryKey: k("hub-youtube"),
    queryFn: () => apiRequest("GET", q("/api/integrations/youtube/status")),
    staleTime: 60_000,
    retry: false,
  });
  const linkedin = useQuery<IntegrationStatusResponse>({
    queryKey: k("hub-linkedin"),
    queryFn: () => apiRequest("GET", q("/api/integrations/linkedin/status")),
    staleTime: 60_000,
    retry: false,
  });
  const allegro = useQuery<IntegrationStatusResponse>({
    queryKey: k("hub-allegro"),
    queryFn: () => apiRequest("GET", q("/api/integrations/allegro/status")),
    staleTime: 60_000,
    retry: false,
    // VP-116: integracja Performance — dla Organica endpoint zwraca 403.
    enabled: showPerformance,
  });
  // Bez `q`/`k` — status Shopify czytamy z konta zalogowanego, bo tam ląduje claim
  // sklepu (SELF_SCOPED_INTEGRATIONS, VP-317).
  const shopify = useQuery<ShopifyStatusResponse>({
    queryKey: ["hub-shopify"],
    queryFn: () => apiRequest("GET", "/api/integrations/shopify/status"),
    staleTime: 60_000,
    retry: false,
    enabled: canSeeShopify && showPerformance,
  });
  const baselinker = useQuery<IntegrationStatusResponse>({
    queryKey: k("hub-baselinker"),
    queryFn: () => apiRequest("GET", q("/api/integrations/baselinker/status")),
    staleTime: 60_000,
    retry: false,
    // VP-116: integracja Performance — dla Organica endpoint zwraca 403.
    enabled: showPerformance,
  });
  const gmc = useQuery<IntegrationStatusResponse>({
    queryKey: k("hub-gmc"),
    queryFn: () => apiRequest("GET", q("/api/integrations/google-merchant/status")),
    staleTime: 60_000,
    retry: false,
    // VP-116: integracja Performance — dla Organica endpoint zwraca 403.
    enabled: showPerformance,
  });
  const ghl = useQuery<IntegrationStatusResponse>({
    queryKey: k("hub-ghl"),
    queryFn: () => apiRequest("GET", q("/api/integrations/crm/status")),
    staleTime: 60_000,
    retry: false,
    // VP-116: integracja Performance — dla Organica endpoint zwraca 403.
    enabled: showPerformance,
  });
  const hubspot = useQuery<IntegrationStatusResponse>({
    queryKey: k("hub-hubspot"),
    queryFn: () => apiRequest("GET", q("/api/integrations/hubspot/status")),
    staleTime: 60_000,
    retry: false,
    // VP-116: integracja Performance — dla Organica endpoint zwraca 403.
    enabled: showPerformance,
  });
  const ringostat = useQuery<IntegrationStatusResponse>({
    queryKey: k("hub-ringostat"),
    queryFn: () => apiRequest("GET", q("/api/integrations/ringostat/status")),
    staleTime: 60_000,
    retry: false,
    // VP-116: integracja Performance — dla Organica endpoint zwraca 403.
    enabled: showPerformance,
  });
  const calendly = useQuery<IntegrationStatusResponse>({
    queryKey: k("hub-calendly"),
    queryFn: () => apiRequest("GET", q("/api/integrations/calendly/status")),
    staleTime: 60_000,
    retry: false,
    // VP-116: integracja Performance — dla Organica endpoint zwraca 403.
    enabled: showPerformance,
  });
  const everwebinar = useQuery<IntegrationStatusResponse>({
    queryKey: k("hub-ew"),
    queryFn: () => apiRequest("GET", q("/api/integrations/everwebinar/status")),
    staleTime: 60_000,
    retry: false,
    // VP-116: integracja Performance — dla Organica endpoint zwraca 403.
    enabled: showPerformance,
  });
  const tracking = useQuery<IntegrationStatusResponse>({
    queryKey: k("hub-tracking"),
    queryFn: () => apiRequest("GET", q("/api/tracking/stats")),
    staleTime: 60_000,
    retry: false,
    // VP-116: integracja Performance — dla Organica endpoint zwraca 403.
    enabled: showPerformance,
  });
  const capiStats = useQuery<IntegrationStatusResponse>({
    queryKey: k("hub-capi"),
    queryFn: () => apiRequest("GET", q("/api/capi/stats?days=7")),
    staleTime: 60_000,
    retry: false,
    // VP-116: integracja Performance — dla Organica endpoint zwraca 403.
    enabled: showPerformance,
  });
  const stripeTracking = useQuery<{ configured: boolean; lastEventType: string | null }>({
    queryKey: k("hub-stripe-tracking"),
    queryFn: () => apiRequest("GET", q("/api/integrations/stripe-tracking/setup")),
    staleTime: 60_000,
    retry: false,
    enabled: showPerformance,
  });
  const apiKeys = useQuery<IntegrationStatusResponse>({
    queryKey: k("hub-api-keys"),
    queryFn: () => apiRequest("GET", q("/api/external/keys")),
    staleTime: 60_000,
    retry: false,
    // VP-116: integracja Performance — dla Organica endpoint zwraca 403.
    enabled: showPerformance,
  });
  const cloudStorage = useQuery<IntegrationStatusResponse>({
    queryKey: k("hub-cloud-storage"),
    queryFn: () => apiRequest("GET", q("/api/integrations/cloud-storage/status")),
    staleTime: 60_000,
    retry: false,
  });
  const slack = useQuery<IntegrationStatusResponse>({
    queryKey: k("hub-slack"),
    queryFn: () => apiRequest("GET", q("/api/integrations/slack")),
    staleTime: 60_000,
    retry: false,
    // VP-116: integracja Performance — dla Organica endpoint zwraca 403.
    enabled: showPerformance,
  });

  const map: Record<string, { connected: boolean; loading: boolean; detail?: string }> = {};

  const set = (
    id: string,
    q: typeof meta,
    check?: (d: IntegrationStatusResponse) => boolean,
    detail?: (d: IntegrationStatusResponse) => string | undefined
  ) => {
    if (q.isLoading) {
      map[id] = { connected: false, loading: true };
      return;
    }
    if (q.data) {
      const d = q.data;
      map[id] = {
        connected: check ? check(d) : !!d.connected,
        loading: false,
        detail: detail
          ? detail(d)
          : d.accountName || d.shopDomain || d.projectName || d.selectedAccount || undefined,
      };
    } else {
      map[id] = { connected: false, loading: false };
    }
  };

  set("meta", meta);
  set("google_ads", gads);
  set("tiktok", tiktok);
  set(
    "tiktok_ads",
    tiktokAds,
    undefined,
    (d) => d.selectedAdvertiserName || d.businessName || undefined
  );
  set("youtube", youtube, undefined, (d) => d.channelTitle || d.googleEmail || undefined);
  set("linkedin", linkedin, undefined, (d) =>
    d.linkedinPublishAs === "organization" && d.linkedinOrganizationName
      ? `Firma: ${d.linkedinOrganizationName}`
      : d.linkedinName || undefined
  );
  set("allegro", allegro);
  // Martwy token: nagłówek karty świeci „Połączono", więc detail musi nieść ostrzeżenie.
  set("shopify", shopify, undefined, (d) => {
    const name = (d.shopName || d.shopDomain || undefined) as string | undefined;
    if (d.needsReauth !== true) return name;
    const warning = statusT("connectPanels.shopify.needsReauthTitle");
    return name ? `${name} · ${warning}` : warning;
  });
  set("baselinker", baselinker);
  set("gmc", gmc);
  set("ghl", ghl);
  set("hubspot", hubspot);
  // Ringostat is webhook-only — don't surface a "Project <id>" detail (legacy API artifact);
  // the connected state + webhook URL panel are the meaningful signals.
  set("ringostat", ringostat, undefined, () => undefined);
  set(
    "calendly",
    calendly,
    undefined,
    (d) => d.calendlyUserName || d.calendlyUserEmail || undefined
  );
  set("everwebinar", everwebinar, undefined, (d) =>
    d.webinar_name ? `${d.webinar_name}` : undefined
  );
  set(
    "slack",
    slack,
    (d) => Array.isArray(d.integrations) && d.integrations.length > 0,
    (d) => {
      if (!Array.isArray(d.integrations) || d.integrations.length === 0) return undefined;
      if (d.integrations.length === 1)
        return d.integrations[0].label || d.integrations[0].teamName || undefined;
      return `${d.integrations.length} workspace'ów`;
    }
  );
  set(
    "tracking",
    tracking,
    (d) => (d.visitors || 0) + (d.events || 0) > 0,
    (d) => (d.visitors ? `${d.visitors} visitors, ${d.events} events` : undefined)
  );
  set(
    "stripe",
    stripeTracking,
    (d) => !!d.configured,
    (d) => (d.lastEventType ? `${d.lastEventType}` : undefined)
  );
  set(
    "capi",
    capiStats,
    (d) => (d.total || 0) > 0,
    (d) => (d.sent ? `${d.sent} sent, ${d.failed} failed (7d)` : undefined)
  );

  // External API keys
  if (apiKeys.isLoading) {
    map["external_api"] = { connected: false, loading: true };
    map["claude_mcp"] = { connected: false, loading: true };
  } else if (apiKeys.data?.keys) {
    const active = apiKeys.data.keys.filter((k) => !k.revoked_at);
    map["external_api"] = {
      connected: active.length > 0,
      loading: false,
      detail: active.length > 0 ? `${active.length} aktywnych kluczy` : undefined,
    };
    const mcpActive = active.filter((k: { scopes?: string[] }) =>
      (k.scopes ?? []).some((scope) => scope.startsWith("scheduler:"))
    );
    map["claude_mcp"] = {
      connected: mcpActive.length > 0,
      loading: false,
      detail: mcpActive.length > 0 ? `${mcpActive.length} aktywnych kluczy MCP` : undefined,
    };
  } else {
    map["external_api"] = { connected: false, loading: false };
    map["claude_mcp"] = { connected: false, loading: false };
  }

  // Cloud storage
  if (cloudStorage.isLoading) {
    map["google_drive"] = { connected: false, loading: true };
  } else if (cloudStorage.data) {
    const gd = cloudStorage.data.google_drive;
    map["google_drive"] = { connected: !!gd?.connected, loading: false, detail: gd?.email };
  } else {
    map["google_drive"] = { connected: false, loading: false };
  }

  return map;
}

// ═══════════════════════════════════════════════════════
// Sub-components
// ═══════════════════════════════════════════════════════

function TypeBadge({ type, label }: { type: ConnectionType; label: string }) {
  const colors: Record<ConnectionType, string> = {
    oauth: "bg-blue-500/15 text-blue-400 border-blue-500/25",
    api_key: "bg-amber-500/15 text-amber-400 border-amber-500/25",
    webhook: "bg-purple-500/15 text-purple-400 border-purple-500/25",
    script: "bg-red-500/15 text-red-400 border-red-500/25",
    config: "bg-cyan-500/15 text-cyan-400 border-cyan-500/25",
    url: "bg-emerald-500/15 text-emerald-400 border-emerald-500/25",
  };
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${colors[type]}`}>
      {label}
    </span>
  );
}

// ═══════════════════════════════════════════════════════
// Conversion payload docs per integration
// ═══════════════════════════════════════════════════════

type ConversionDoc = {
  label: string;
  event: string;
  colorClass: string;
  payload: string;
};

const CONVERSION_DOCS: Record<string, ConversionDoc[]> = {
  zapier: [
    {
      label: "Lead",
      event: "POST /api/webhooks/external",
      colorClass: "text-blue-400 bg-blue-500/10 border-blue-500/30",
      payload: `{
  "event":        "lead",
  "email":        "jan@firma.pl",       // wymagane
  "phone":        "+48123456789",
  "first_name":   "Jan",
  "last_name":    "Kowalski",
  "utm_source":   "facebook",
  "utm_campaign": "kampania_lato",
  "utm_medium":   "cpc",
  "utm_content":  "ad_variant_A"
}`,
    },
    {
      label: "convDocs.salesCall",
      event: "POST /api/webhooks/external",
      colorClass: "text-orange-400 bg-orange-500/10 border-orange-500/30",
      payload: `{
  "event":        "booked_call",
  "email":        "jan@firma.pl",       // wymagane
  "phone":        "+48123456789",
  "call_date":    "2024-04-15T10:00:00Z",
  "setter_name":  "Anna Kowalska",
  "utm_source":   "facebook",
  "utm_campaign": "kampania_lato"
}`,
    },
    {
      label: "convDocs.sale",
      event: "POST /api/webhooks/external",
      colorClass: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
      payload: `{
  "event":        "purchase",
  "email":        "jan@firma.pl",       // wymagane
  "phone":        "+48123456789",
  "order_id":     "ORD-12345",
  "revenue":      4997.00,
  "currency":     "PLN",
  "product_name": "Kurs Digital Marketing",
  "utm_source":   "facebook",
  "utm_campaign": "kampania_lato"
}`,
    },
  ],
  calendly: [
    {
      label: "convDocs.bookedCall",
      event: "invitee.created (automatic)",
      colorClass: "text-orange-400 bg-orange-500/10 border-orange-500/30",
      payload: `// Sent automatically by Calendly after OAuth connection:
{
  "email":      "jan@firma.pl",     // z Calendly
  "name":       "Jan Kowalski",     // z Calendly
  "event_type": "Rozmowa kwalifikacyjna",
  "start_time": "2024-04-15T10:00:00Z",
  "tracking": {
    "utm_source":   "facebook",     // if the Calendly link has UTMs
    "utm_campaign": "kampania_lato"
  }
}`,
    },
    {
      label: "convDocs.canceledCall",
      event: "invitee.canceled (automatic)",
      colorClass: "text-red-400 bg-red-500/10 border-red-500/30",
      payload: `// Sent automatically by Calendly when canceled.
// email + cancel_reason are sent to the dashboard.
// No additional configuration needed on your end.`,
    },
  ],
  hubspot: [
    {
      label: "Lead",
      event: "Workflow → Webhook on contact.creation",
      colorClass: "text-blue-400 bg-blue-500/10 border-blue-500/30",
      payload: `// HubSpot → Automation → Workflows → Webhook
{
  "event":        "lead",
  "email":        "{{ contact.email }}",       // wymagane
  "phone":        "{{ contact.phone }}",
  "first_name":   "{{ contact.firstname }}",
  "last_name":    "{{ contact.lastname }}",
  "utm_source":   "{{ contact.utm_source__c }}",
  "utm_campaign": "{{ contact.utm_campaign__c }}",
  "utm_medium":   "{{ contact.utm_medium__c }}"
}`,
    },
    {
      label: "convDocs.salesCall",
      event: 'Workflow → stage = "Appointment Scheduled"',
      colorClass: "text-orange-400 bg-orange-500/10 border-orange-500/30",
      payload: `// Trigger: etap dealu = "Appointment Scheduled"
{
  "event":        "booked_call",
  "email":        "{{ contact.email }}",
  "phone":        "{{ contact.phone }}",
  "deal_name":    "{{ deal.dealname }}",
  "call_date":    "{{ deal.closedate }}",
  "setter_name":  "{{ owner.firstname }} {{ owner.lastname }}",
  "utm_campaign": "{{ contact.utm_campaign__c }}"
}`,
    },
    {
      label: "convDocs.sale",
      event: 'Workflow → dealstage = "closedwon"',
      colorClass: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
      payload: `// Trigger: etap dealu = "Closed Won"
{
  "event":        "purchase",
  "email":        "{{ contact.email }}",
  "phone":        "{{ contact.phone }}",
  "order_id":     "{{ deal.hs_deal_id }}",
  "revenue":      {{ deal.amount }},
  "currency":     "PLN",
  "product_name": "{{ deal.dealname }}",
  "utm_source":   "{{ contact.utm_source__c }}",
  "utm_campaign": "{{ contact.utm_campaign__c }}"
}`,
    },
  ],
};

function ConversionDocsSection({ integrationId }: { integrationId: string }) {
  const { t } = useTranslation("integrationsHub");
  const [activeTab, setActiveTab] = useState(0);
  const docs = CONVERSION_DOCS[integrationId];
  if (!docs) return null;

  const active = docs[activeTab] as ConversionDoc;

  return (
    <div className="space-y-2 mt-1">
      <p className="text-xs font-semibold text-white/60 uppercase tracking-wide">
        {t("convDocs.title")}
      </p>
      <div className="flex gap-1.5 flex-wrap">
        {docs.map((doc, i) => (
          <button
            key={doc.label}
            onClick={() => setActiveTab(i)}
            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
              i === activeTab
                ? doc.colorClass
                : "text-white/40 bg-white/[0.03] border-white/[0.08] hover:text-white/60"
            }`}
          >
            {t(doc.label)}
          </button>
        ))}
      </div>
      <div className="rounded-lg border border-white/[0.08] bg-black/20 overflow-hidden">
        <div className="px-3 py-1.5 border-b border-white/[0.06]">
          <span className="text-[10px] font-mono text-white/40">{active.event}</span>
        </div>
        <pre className="text-[11px] font-mono text-emerald-300/90 p-3 overflow-x-auto whitespace-pre-wrap leading-relaxed">
          {active.payload}
        </pre>
      </div>
    </div>
  );
}

const CONNECT_URLS: Record<string, string> = {
  meta: "/api/integrations/meta/connect",
  google_ads: "/api/integrations/google-ads/connect",
  tiktok: "/api/integrations/tiktok/connect",
  tiktok_ads: "/api/integrations/tiktok-ads/connect",
  youtube: "/api/integrations/youtube/connect",
  linkedin: "/api/integrations/linkedin/connect",
  allegro: "/api/integrations/allegro/connect",
  gmc: "/api/integrations/google-merchant/connect",
  ghl: "/api/integrations/crm/connect",
  calendly: "/api/integrations/calendly/connect",
  hubspot: "/api/integrations/hubspot/connect",
  google_drive: "/api/integrations/cloud-storage/connect/google_drive",
  slack: "/api/integrations/slack/connect",
};

interface DisconnectConfig {
  url: string;
  method: "POST" | "DELETE";
}

interface LinkedInOrganization {
  id: string;
  urn: string;
  name: string;
  role?: string;
}

interface LinkedInOrganizationsResponse {
  organizations: LinkedInOrganization[];
  publishAs?: "person" | "organization";
  selectedOrganizationId?: string | null;
  selectedOrganizationName?: string | null;
}

async function fetchLinkedInOrganizationsJson(): Promise<LinkedInOrganizationsResponse> {
  const authToken = getAuthToken();
  const res = await fetch("/api/integrations/linkedin/organizations", {
    headers: {
      "Content-Type": "application/json",
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
  });

  const text = await res.text();
  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("text/html") || text.trim().startsWith("<!DOCTYPE")) {
    throw new Error(
      "Endpoint /api/integrations/linkedin/organizations zwrócił HTML. Backend prawdopodobnie nie ma wdrożonej najnowszej wersji z obsługą stron firmowych LinkedIn."
    );
  }

  // Odpowiedz bledu ma inny ksztalt niz sukcesu — stad unia zamiast castu na sukces.
  let payload: Partial<LinkedInOrganizationsResponse> & { message?: string; error?: string } = {};
  try {
    payload = text ? JSON.parse(text) : {};
  } catch {
    throw new Error(`Endpoint LinkedIn zwrócił niepoprawny JSON: ${text.slice(0, 180)}`);
  }

  if (!res.ok) {
    throw new Error(payload?.message || payload?.error || res.statusText);
  }

  return payload as LinkedInOrganizationsResponse;
}

// Inline connect panels for non-OAuth integrations. Missing entries fall back
// to the generic "configure in analytics settings" hint inside IntegrationCard.
const INLINE_CONNECT_PANELS: Record<string, (props: { forUserId?: string }) => ReactNode> = {
  shopify: ({ forUserId }) => <ShopifyConnectPanel forUserId={forUserId} autoLoadInsights />,
  baselinker: ({ forUserId }) => <BaseLinkerConnectPanel forUserId={forUserId} />,
};

const DISCONNECT_URLS: Record<string, DisconnectConfig> = {
  meta: { url: "/api/integrations/meta/disconnect", method: "POST" },
  google_ads: { url: "/api/integrations/google-ads/disconnect", method: "POST" },
  tiktok: { url: "/api/integrations/tiktok/disconnect", method: "POST" },
  tiktok_ads: { url: "/api/integrations/tiktok-ads/disconnect", method: "POST" },
  youtube: { url: "/api/integrations/youtube/disconnect", method: "POST" },
  linkedin: { url: "/api/integrations/linkedin/disconnect", method: "POST" },
  allegro: { url: "/api/integrations/allegro/disconnect", method: "POST" },
  shopify: { url: "/api/integrations/shopify/disconnect", method: "POST" },
  baselinker: { url: "/api/integrations/baselinker/disconnect", method: "POST" },
  gmc: { url: "/api/integrations/google-merchant/disconnect", method: "POST" },
  ghl: { url: "/api/integrations/crm/disconnect", method: "POST" },
  hubspot: { url: "/api/integrations/hubspot/disconnect", method: "POST" },
  // Ringostat is webhook-only — there is nothing to "disconnect" (no OAuth token); the
  // webhook URL simply exists. No disconnect button — the URL is always shown.
  calendly: { url: "/api/integrations/calendly/disconnect", method: "POST" },
  everwebinar: { url: "/api/integrations/everwebinar/disconnect", method: "POST" },
  google_drive: {
    url: "/api/integrations/cloud-storage/disconnect/google_drive",
    method: "DELETE",
  },
  // Slack disconnect is per-workspace and lives inside SlackSettingsSection (multi-workspace UI).
};

// Real brand logos via Google's favicon service (universal, no bundled assets). Falls back
// to the emoji glyph when the brand has no mapped domain or the logo fails to load.
const LOGO_DOMAINS: Record<string, string> = {
  meta: "facebook.com",
  google_ads: "ads.google.com",
  tiktok: "tiktok.com",
  tiktok_ads: "tiktok.com",
  youtube: "youtube.com",
  linkedin: "linkedin.com",
  allegro: "allegro.pl",
  shopify: "shopify.com",
  baselinker: "baselinker.com",
  gmc: "merchants.google.com",
  ghl: "gohighlevel.com",
  hubspot: "hubspot.com",
  ringostat: "ringostat.com",
  calendly: "calendly.com",
  everwebinar: "everwebinar.com",
  n8n_ideas: "n8n.io",
  slack: "slack.com",
};

function IntegrationLogo({
  integration,
  grayscale,
}: {
  integration: Integration;
  grayscale?: boolean;
}) {
  const [failed, setFailed] = useState(false);
  const domain = LOGO_DOMAINS[integration.id];
  if (!domain || failed) {
    return (
      <span className={`text-xl flex-shrink-0 ${grayscale ? "grayscale" : ""}`}>
        {integration.icon}
      </span>
    );
  }
  return (
    <img
      src={`https://www.google.com/s2/favicons?sz=64&domain=${domain}`}
      alt=""
      width={28}
      height={28}
      loading="lazy"
      onError={() => setFailed(true)}
      className={`w-7 h-7 flex-shrink-0 rounded ${grayscale ? "grayscale" : ""}`}
    />
  );
}

function IntegrationCard({
  integration,
  status,
  planUsage,
  onOpenCapi,
  locked,
  onLockClick,
  initialOpen,
  forUserId,
}: {
  integration: Integration;
  status?: { connected: boolean; loading: boolean; detail?: string };
  planUsage?: Record<string, PlatformUsage>;
  onOpenCapi?: () => void;
  locked?: boolean;
  onLockClick?: () => void;
  initialOpen?: boolean;
  forUserId?: string | null;
}) {
  const [open, setOpen] = useState(!!initialOpen);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const queryClient = useQueryClient();
  const { t } = useTranslation("integrationsHub");
  const { toast } = useToast();
  const { selectedClientName } = useStore();
  const isConnected = status?.connected ?? false;
  // VP-311: hub z jednym klientem auto-wybiera go (useAgencyClients), więc OAuth ląduje
  // na koncie klienta, a nie zalogowanego usera. Bez tej informacji „Połączono" na karcie
  // i puste dane na własnym koncie wyglądają jak nieudane łączenie.
  const savesToOwnAccount = !forUserId || CONNECT_IGNORES_CLIENT_SCOPE.has(integration.id);
  const contextLabel = savesToOwnAccount
    ? t("oauthSuccess.contextOwn")
    : selectedClientName
      ? t("oauthSuccess.contextClient", { name: selectedClientName })
      : t("oauthSuccess.contextClientUnknown");
  const isLoading = status?.loading ?? false;
  const platformKey = INTEGRATION_TO_PLATFORM[integration.id];
  const platformUsage = platformKey ? planUsage?.[platformKey] : undefined;
  const atPlanLimit =
    !!platformUsage &&
    platformUsage.limit !== null &&
    platformUsage.used >= platformUsage.limit &&
    !isConnected;
  // Status query keys are tagged with forUserId in useIntegrationStatuses, so
  // we mirror that here to invalidate the correct entry per agency client.
  const baseStatusKey =
    integration.id === "google_drive" ? "hub-cloud-storage" : "hub-" + integration.id;
  const statusQueryKey = forUserId ? [baseStatusKey, forUserId] : [baseStatusKey];

  // Append ?forUserId=<clientUserId> to integration endpoints when the agency
  // user is acting on behalf of a client. Falls back to plain URL otherwise.
  const withForUser = (url: string): string => {
    if (!forUserId) return url;
    return `${url}${url.includes("?") ? "&" : "?"}forUserId=${forUserId}`;
  };

  const invalidateIntegrationStatus = () => {
    queryClient.invalidateQueries({ queryKey: statusQueryKey });
    queryClient.invalidateQueries({ queryKey: ["integrations-statuses"] });
    if (integration.id === "linkedin") {
      queryClient.invalidateQueries({ queryKey: ["hub-linkedin-organizations"] });
    }
  };

  const handleDisconnect = async () => {
    const config = DISCONNECT_URLS[integration.id];
    if (!config) return;
    setDisconnecting(true);
    try {
      await apiRequest(config.method, withForUser(config.url));
      invalidateIntegrationStatus();
    } catch {
      // The integration status remains unchanged; the user can retry the action.
    } finally {
      setDisconnecting(false);
      setConfirmDisconnect(false);
    }
  };

  if (locked) {
    return (
      <div
        id={integration.id}
        className="border rounded-xl overflow-hidden border-white/[0.06] bg-white/[0.01] opacity-60 cursor-pointer hover:opacity-80 hover:border-amber-500/20 transition-all"
        onClick={onLockClick}
      >
        <div className="w-full flex items-center gap-dense p-4">
          <IntegrationLogo integration={integration} grayscale />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-white/50">{integration.name}</span>
              <TypeBadge type={integration.type} label={integration.typeLabel} />
            </div>
            <div className="text-xs text-white/30 mt-0.5 truncate">{t(integration.desc)}</div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <Lock className="w-3.5 h-3.5 text-amber-400/70" />
            <span className="text-xs text-amber-400/70 hidden sm:inline">
              {t("card.changePlan")}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      id={integration.id}
      className={`border rounded-xl overflow-hidden transition-colors ${
        isConnected
          ? "bg-emerald-500/[0.04] border-emerald-500/20 hover:border-emerald-500/30"
          : "bg-white/[0.02] border-white/[0.08] hover:border-white/[0.15]"
      }`}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-dense p-4 text-left"
      >
        <IntegrationLogo integration={integration} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-white">{integration.name}</span>
            <TypeBadge type={integration.type} label={integration.typeLabel} />
          </div>
          <div className="text-xs text-white/50 mt-0.5 truncate">{t(integration.desc)}</div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isLoading ? (
            <div className="flex items-center gap-1.5">
              <Skeleton className="w-2 h-2 rounded-full" />
              <Skeleton className="h-3 w-20 hidden sm:block" />
            </div>
          ) : isConnected ? (
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-xs text-emerald-400 font-medium hidden sm:inline">
                {t("card.connected")}
              </span>
            </div>
          ) : status ? (
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-white/20" />
              <span className="text-xs text-white/40 hidden sm:inline">
                {t("card.notConnected")}
              </span>
            </div>
          ) : null}
          {open ? (
            <ChevronUp className="w-4 h-4 text-white/30" />
          ) : (
            <ChevronDown className="w-4 h-4 text-white/30" />
          )}
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 border-t border-white/[0.06] space-y-3 pt-3">
          {isConnected && status?.detail && (
            <div className="flex items-center gap-2 p-2.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
              <Check className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
              <span className="text-xs text-emerald-300">{status.detail}</span>
            </div>
          )}

          {integration.helpText && (
            <div className="p-3 bg-white/[0.03] border border-white/[0.08] rounded-lg">
              <p className="text-xs text-white/60 leading-relaxed">
                {integration.helpText ? t(integration.helpText) : ""}
              </p>
            </div>
          )}

          {integration.id === "linkedin" && isConnected && <LinkedInPublishTargetPanel />}

          {integration.id === "tiktok_ads" && isConnected && (
            <TikTokAdsPanel forUserId={forUserId ?? null} />
          )}

          {integration.type === "oauth" &&
            CONNECT_URLS[integration.id] &&
            !isConnected &&
            atPlanLimit && (
              <div
                className="inline-flex items-center gap-2 px-4 py-2 bg-white/[0.04] border border-amber-500/30 text-amber-300 text-xs font-medium rounded-lg cursor-not-allowed"
                title={`Limit planu osiągnięty (${platformUsage?.used}/${platformUsage?.limit}). Odłącz istniejące konto lub zmień plan.`}
              >
                <Lock className="w-3.5 h-3.5" />
                Limit planu: {platformUsage?.used}/{platformUsage?.limit}
              </div>
            )}

          {integration.type === "oauth" &&
            CONNECT_URLS[integration.id] &&
            !isConnected &&
            !atPlanLimit && (
              <button
                onClick={() => {
                  const w = 600,
                    h = 700;
                  const left = window.screenX + (window.outerWidth - w) / 2;
                  const top = window.screenY + (window.outerHeight - h) / 2;
                  const popup = window.open(
                    "about:blank",
                    `${integration.id}_oauth`,
                    `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no`
                  );
                  // Write loading page immediately so browser doesn't auto-close blank popup
                  if (popup) {
                    popup.document.write(
                      '<html><head><title>Connecting...</title></head><body style="background:#111;color:#fff;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;font-family:sans-serif"><p>Connecting to ' +
                        escapeHtml(integration.name) +
                        "...</p></body></html>"
                    );
                    popup.document.close();
                  }
                  const checkClosed = setInterval(() => {
                    if (popup?.closed) {
                      clearInterval(checkClosed);
                      invalidateIntegrationStatus();
                    }
                  }, 500);

                  const messageHandler = (event: MessageEvent) => {
                    const eventPrefix = integration.id.toUpperCase().replace(/-/g, "_");
                    const successType = eventPrefix + "_OAUTH_SUCCESS";
                    // VP-311: bez tej gałęzi popup zgłaszał błąd, zamykał się, a użytkownik
                    // widział tylko znikające okno i dalej „Nie połączono".
                    if (event.data?.type === eventPrefix + "_OAUTH_ERROR") {
                      window.removeEventListener("message", messageHandler);
                      clearInterval(checkClosed);
                      invalidateIntegrationStatus();
                      toast({
                        title: t("oauthError.title"),
                        description: t("oauthError.description", {
                          error: String(event.data.error ?? ""),
                        }),
                        variant: "destructive",
                      });
                      return;
                    }
                    const isCloudStorageSuccess =
                      integration.id === "google_drive" &&
                      event.data?.type === "cloud_storage_callback" &&
                      event.data?.success;
                    if (
                      event.data?.type === successType ||
                      event.data?.type === "HUBSPOT_OAUTH_SUCCESS" ||
                      isCloudStorageSuccess
                    ) {
                      window.removeEventListener("message", messageHandler);
                      clearInterval(checkClosed);
                      invalidateIntegrationStatus();
                      // VP-311: sukces i błąd wyglądały dla usera identycznie (okno znika,
                      // karta bez zmiany) — stąd zgłoszenia „nie łączy" przy udanym OAuth.
                      toast({
                        title: t("oauthSuccess.title"),
                        description: t("oauthSuccess.description", {
                          name: integration.name,
                          context: contextLabel,
                        }),
                      });
                    }
                  };
                  window.addEventListener("message", messageHandler);

                  apiRequest("GET", withForUser(CONNECT_URLS[integration.id]))
                    .then((data) => {
                      const connectUrl = (data as { url?: string })?.url;
                      if (connectUrl && popup && !popup.closed) {
                        popup.location.href = connectUrl;
                      }
                    })
                    .catch((err: unknown) => {
                      clearInterval(checkClosed);
                      window.removeEventListener("message", messageHandler);
                      if (popup && !popup.closed) {
                        try {
                          popup.document.body.innerHTML = `<div style="padding:20px;font-family:sans-serif;color:#333"><h3>Connection error</h3><p>${escapeHtml(err instanceof Error ? err.message : "Unknown error")}</p><p>Close this window and try again.</p></div>`;
                        } catch {
                          popup.close();
                        }
                      }
                    });
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded-lg transition-colors"
              >
                <Plug className="w-3.5 h-3.5" />
                {t("card.connect")}
              </button>
            )}

          {isConnected && DISCONNECT_URLS[integration.id] && (
            <div className="flex items-center gap-2">
              {!confirmDisconnect ? (
                <button
                  onClick={() => setConfirmDisconnect(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/[0.05] hover:bg-red-500/15 border border-white/[0.1] hover:border-red-500/30 text-white/50 hover:text-red-400 text-xs rounded-lg transition-all"
                >
                  <Unplug className="w-3 h-3" />
                  {t("card.disconnect")}
                </button>
              ) : (
                <div className="flex items-center gap-2 p-2.5 bg-red-500/10 border border-red-500/20 rounded-lg w-full">
                  <span className="text-xs text-red-300 flex-1">
                    {t("card.confirmDisconnect", { name: integration.name })}
                  </span>
                  <button
                    onClick={handleDisconnect}
                    disabled={disconnecting}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-medium rounded transition-colors"
                  >
                    {disconnecting ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Unplug className="w-3 h-3" />
                    )}
                    {t("card.confirmYes")}
                  </button>
                  <button
                    onClick={() => setConfirmDisconnect(false)}
                    className="px-3 py-1 bg-white/[0.05] hover:bg-white/[0.1] text-white/60 text-xs rounded transition-colors"
                  >
                    {t("card.cancel")}
                  </button>
                </div>
              )}
            </div>
          )}

          {integration.id === "meta" && isConnected && (
            <MetaConversionMappingPanel forUserId={forUserId ?? null} />
          )}

          {/* Po podłączeniu panel Shopify musi zostać: to w nim jest baner „wymaga
              ponownego połączenia" (martwy token) i brakujące scope'y — generyczne body
              karty pokazywało tylko helpText i „Odłącz". */}
          {integration.id === "shopify" && isConnected && (
            <ShopifyConnectPanel forUserId={forUserId ?? undefined} autoLoadInsights />
          )}

          {integration.id === "meta" && <MetaResubscribeAllPanel />}

          {integration.id === "hubspot" && isConnected && <HubspotStageMappingPanel />}

          {integration.id === "ringostat" && isConnected && (
            <RingostatConnectPanel forUserId={forUserId ?? undefined} />
          )}

          {integration.id === "ghl" && isConnected && (
            <>
              <GhlSubaccountsPanel forUserId={forUserId ?? null} />
              <GhlCalendarSelectionPanel forUserId={forUserId ?? null} />
              <GhlTagsGuidePanel />
              <GhlWebhookSetupPanel />
            </>
          )}

          {integration.id === "slack" && <SlackSettingsSection forUserId={forUserId ?? null} />}

          {!isConnected &&
            INLINE_CONNECT_PANELS[integration.id]?.({ forUserId: forUserId ?? undefined })}

          {(integration.type === "api_key" ||
            integration.type === "config" ||
            integration.type === "url") &&
            !isConnected &&
            !INLINE_CONNECT_PANELS[integration.id] &&
            integration.id !== "claude_mcp" && (
              <p className="text-xs text-white/40">
                Skonfiguruj w Ustawieniach analityki (⚙️ na dashboardzie).
              </p>
            )}

          {integration.id === "tracking" && (
            <TrackingConnectPanel defaultExpanded forUserId={forUserId ?? undefined} />
          )}

          {integration.id === "capi" && onOpenCapi && (
            <button
              onClick={onOpenCapi}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded-lg transition-colors"
            >
              <Send className="w-3.5 h-3.5" />
              {isConnected ? t("card.manageCapi") : t("card.configureCapi")}
            </button>
          )}

          {integration.id === "external_api" && <ClientApiPanel />}

          {integration.id === "claude_mcp" && <McpKeyPanel />}

          {integration.id === "crm_webhook" && <CrmWebhookSetupPanel />}

          {integration.id === "stripe" && (
            <StripeTrackingSetupPanel forUserId={forUserId ?? undefined} />
          )}

          {integration.id === "everwebinar" && <EverwebinarConfigPanel />}

          {CONVERSION_DOCS[integration.id] && (
            <ConversionDocsSection integrationId={integration.id} />
          )}
        </div>
      )}
    </div>
  );
}

function LinkedInPublishTargetPanel() {
  const queryClient = useQueryClient();
  const status = useQuery<IntegrationStatusResponse>({
    queryKey: ["hub-linkedin"],
    queryFn: () => apiRequest("GET", "/api/integrations/linkedin/status"),
    staleTime: 60_000,
    retry: false,
  });
  const hasOrganizationScopes = hasLinkedInOrganizationScopes(status.data?.scopes);
  const organizations = useQuery<LinkedInOrganizationsResponse>({
    queryKey: ["hub-linkedin-organizations"],
    queryFn: fetchLinkedInOrganizationsJson,
    staleTime: 5 * 60 * 1000,
    retry: false,
    enabled: Boolean(status.data?.connected && hasOrganizationScopes),
  });
  const updateTarget = useMutation({
    mutationFn: (payload: {
      publishAs: "person" | "organization";
      organizationId?: string;
      organizationName?: string;
    }) => apiRequest("POST", "/api/integrations/linkedin/publish-target", payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hub-linkedin"] });
      queryClient.invalidateQueries({ queryKey: ["hub-linkedin-organizations"] });
      queryClient.invalidateQueries({ queryKey: ["/api/scheduler/connected-platforms"] });
    },
  });

  const profileName = status.data?.linkedinName || "Twój profil";
  const orgRows = organizations.data?.organizations ?? [];
  const selectedValue =
    status.data?.linkedinPublishAs === "organization" && status.data?.linkedinOrganizationId
      ? status.data.linkedinOrganizationId
      : "person";
  const errorMessage = organizations.error instanceof Error ? organizations.error.message : null;

  const handleReconnect = async () => {
    const w = 600,
      h = 700;
    const left = window.screenX + (window.outerWidth - w) / 2;
    const top = window.screenY + (window.outerHeight - h) / 2;
    const popup = window.open(
      "about:blank",
      "linkedin_oauth_reconnect",
      `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no`
    );
    if (popup) {
      popup.document.write(
        '<html><head><title>Łączenie...</title></head><body style="background:#111;color:#fff;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;font-family:sans-serif"><p>Łączenie z LinkedIn...</p></body></html>'
      );
      popup.document.close();
    }

    const refreshLinkedInQueries = () => {
      queryClient.invalidateQueries({ queryKey: ["hub-linkedin"] });
      queryClient.invalidateQueries({ queryKey: ["hub-linkedin-organizations"] });
      queryClient.invalidateQueries({ queryKey: ["/api/scheduler/connected-platforms"] });
    };

    const checkClosed = setInterval(() => {
      if (popup?.closed) {
        clearInterval(checkClosed);
        refreshLinkedInQueries();
      }
    }, 500);

    const messageHandler = (event: MessageEvent) => {
      if (event.data?.type === "LINKEDIN_OAUTH_SUCCESS") {
        window.removeEventListener("message", messageHandler);
        clearInterval(checkClosed);
        refreshLinkedInQueries();
      }
    };
    window.addEventListener("message", messageHandler);

    try {
      const data = await apiRequest("GET", "/api/integrations/linkedin/connect");
      if (data?.url && popup && !popup.closed) popup.location.href = data.url;
    } catch (err) {
      clearInterval(checkClosed);
      window.removeEventListener("message", messageHandler);
      if (popup && !popup.closed) {
        popup.document.body.innerHTML = `<div style="padding:20px;font-family:sans-serif;color:#333"><h3>Błąd połączenia</h3><p>${escapeHtml((err as Error)?.message || "Nieznany błąd")}</p><p>Zamknij to okno i spróbuj ponownie.</p></div>`;
      }
    }
  };

  const handleTargetChange = (value: string) => {
    if (value === "person") {
      updateTarget.mutate({ publishAs: "person" });
      return;
    }

    const organization = orgRows.find((item) => item.id === value);
    if (!organization) return;
    updateTarget.mutate({
      publishAs: "organization",
      organizationId: organization.id,
      organizationName: organization.name,
    });
  };

  return (
    <div className="p-3 bg-black/20 border border-white/[0.08] rounded-lg space-y-2">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-medium text-white/75">Publikuj w Schedulerze jako</p>
          <p className="text-[11px] text-white/40">
            LinkedIn nie pyta o firmę w logowaniu. Wybór robisz tutaj.
          </p>
        </div>
        {(organizations.isLoading || updateTarget.isPending) && (
          <Loader2 className="w-3.5 h-3.5 animate-spin text-white/35" />
        )}
      </div>

      {!hasOrganizationScopes && (
        <div className="rounded-lg border border-amber-400/20 bg-amber-400/10 p-2.5 space-y-2">
          <p className="text-[11px] text-amber-100/85 leading-relaxed">
            To konto LinkedIn jest połączone starym tokenem bez uprawnień firmowych. Trzeba ponownie
            przejść OAuth, żeby LinkedIn wydał token z dostępem do stron.
          </p>
          <button
            type="button"
            onClick={handleReconnect}
            className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500/20 px-3 py-1.5 text-[11px] font-medium text-amber-100 hover:bg-amber-500/30 transition-colors"
          >
            <Plug className="w-3 h-3" />
            Połącz ponownie LinkedIn
          </button>
          <p className="text-[10px] text-white/35 break-words">
            Aktualne scope’y: {status.data?.scopes || "brak"}
          </p>
        </div>
      )}

      <select
        value={selectedValue}
        onChange={(event) => handleTargetChange(event.target.value)}
        disabled={!hasOrganizationScopes || organizations.isLoading || updateTarget.isPending}
        className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-400/40 disabled:opacity-50"
      >
        <option value="person">Profil: {profileName}</option>
        {orgRows.map((organization) => (
          <option key={organization.id} value={organization.id}>
            Firma: {organization.name}
          </option>
        ))}
      </select>

      {errorMessage && (
        <div className="space-y-1">
          <p className="text-[11px] text-amber-300/80 leading-relaxed">
            Nie udało się pobrać stron firmowych. To nie musi oznaczać, że nie jesteś adminem —
            LinkedIn często blokuje to po stronie uprawnień aplikacji developerskiej.
          </p>
          <p className="text-[10px] text-white/35 break-words font-mono">
            {errorMessage.replace(/^\d+:\s*/, "")}
          </p>
        </div>
      )}

      {!organizations.isLoading && !errorMessage && orgRows.length === 0 && (
        <p className="text-[11px] text-white/40 leading-relaxed">
          Nie znaleziono stron firmowych dla tego konta. Sprawdź, czy konto jest adminem strony i
          czy aplikacja LinkedIn ma scope’y organizacyjne.
        </p>
      )}
    </div>
  );
}

function hasLinkedInOrganizationScopes(scopes: string | null | undefined): boolean {
  const scopeSet = new Set(
    (scopes ?? "")
      .split(/[,\s]+/)
      .map((scope) => scope.trim())
      .filter(Boolean)
  );
  return scopeSet.has("w_organization_social") && scopeSet.has("r_organization_admin");
}

/**
 * TikTok Ads connected panel: pick which advertiser account to pull leads from,
 * and trigger a manual sync of TikTok Lead Gen Instant Forms into the CRM.
 * Mirrors the GHL lead-pull / LinkedIn target panels — the sync is synchronous
 * (backend returns a TikTokSyncSummary, no job/poll), so we just await + report.
 */
type TikTokAdvertiser = { id: string; name: string };
type TikTokAdsStatus = {
  connected: boolean;
  businessName?: string | null;
  selectedAdvertiserId?: string | null;
  selectedAdvertiserName?: string | null;
  advertiserAccounts: TikTokAdvertiser[];
};
type TikTokSyncSummary = {
  totalImported: number;
  totalSkipped: number;
  forms: {
    pageId: string;
    formName?: string | null;
    imported: number;
    skipped: number;
    error?: string;
  }[];
  errors: { advertiserId?: string; pageId?: string; message: string }[];
};

function TikTokAdsPanel({ forUserId }: { forUserId: string | null }) {
  const queryClient = useQueryClient();
  const qs = forUserId ? `?forUserId=${encodeURIComponent(forUserId)}` : "";
  const statusKey = forUserId ? ["hub-tiktok_ads", forUserId] : ["hub-tiktok_ads"];

  const status = useQuery<TikTokAdsStatus>({
    queryKey: statusKey,
    queryFn: () => apiRequest("GET", `/api/integrations/tiktok-ads/status${qs}`),
    staleTime: 60_000,
    retry: false,
  });

  const selectAdvertiser = useMutation({
    mutationFn: (advertiser: TikTokAdvertiser) =>
      apiRequest("POST", "/api/integrations/tiktok-ads/select-advertiser", {
        advertiserId: advertiser.id,
        advertiserName: advertiser.name,
        forUserId,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: statusKey }),
  });

  const syncLeads = useMutation<TikTokSyncSummary>({
    mutationFn: () => apiRequest("POST", "/api/tiktok/leadgen/sync-all", { forUserId }),
  });

  const subscribeRealtime = useMutation<{
    subscribed: string[];
    errors: { advertiserId: string; message: string }[];
  }>({
    mutationFn: () => apiRequest("POST", "/api/tiktok/leadgen/subscribe", { forUserId }),
  });

  const advertisers = status.data?.advertiserAccounts ?? [];
  const selectedId = status.data?.selectedAdvertiserId ?? "";
  const summary = syncLeads.data;
  const syncErrorMessage = syncLeads.error instanceof Error ? syncLeads.error.message : null;
  const subResult = subscribeRealtime.data;
  const subErrorMessage =
    subscribeRealtime.error instanceof Error ? subscribeRealtime.error.message : null;

  const handleSelect = (advertiserId: string) => {
    const advertiser = advertisers.find((a) => a.id === advertiserId);
    if (!advertiser) return;
    selectAdvertiser.mutate(advertiser);
  };

  return (
    <div className="p-3 bg-black/20 border border-white/[0.08] rounded-lg space-y-3">
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs font-medium text-white/75">Konto reklamowe</p>
          {selectAdvertiser.isPending && (
            <Loader2 className="w-3.5 h-3.5 animate-spin text-white/35" />
          )}
        </div>
        {status.isLoading ? (
          <Skeleton className="h-[34px] w-full rounded-lg" />
        ) : advertisers.length === 0 ? (
          <p className="text-[11px] text-white/40 leading-relaxed">
            Brak kont reklamowych dla tego połączenia. Sprawdź, czy konto TikTok for Business ma
            dostęp do konta reklamowego.
          </p>
        ) : (
          <select
            value={selectedId}
            onChange={(event) => handleSelect(event.target.value)}
            disabled={selectAdvertiser.isPending || status.isLoading}
            className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-400/40 disabled:opacity-50"
          >
            <option value="" disabled>
              Wybierz konto reklamowe…
            </option>
            {advertisers.map((advertiser) => (
              <option key={advertiser.id} value={advertiser.id}>
                {advertiser.name} ({advertiser.id})
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => syncLeads.mutate()}
          disabled={syncLeads.isPending || !selectedId}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-sky-500/20 text-sky-200 hover:bg-sky-500/30 border border-sky-500/30 disabled:opacity-50"
        >
          {syncLeads.isPending ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <Zap className="w-3 h-3" />
          )}
          {syncLeads.isPending ? "Synchronizuję…" : "Synchronizuj leady"}
        </button>
        {summary && (
          <span className="text-[11px] text-white/60">
            Zaimportowano <b className="text-white">{summary.totalImported}</b> leadów (pominięto{" "}
            {summary.totalSkipped})
          </span>
        )}
        <button
          type="button"
          onClick={() => subscribeRealtime.mutate()}
          disabled={subscribeRealtime.isPending}
          title="Subskrybuje leady TikTok w czasie rzeczywistym (webhook) — leady wpadają same"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30 border border-emerald-500/30 disabled:opacity-50"
        >
          {subscribeRealtime.isPending ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <Zap className="w-3 h-3" />
          )}
          {subscribeRealtime.isPending ? "Włączam…" : "Włącz real-time"}
        </button>
        {subResult && (
          <span className="text-[11px] text-white/60">
            Real-time włączony dla <b className="text-white">{subResult.subscribed.length}</b> kont
            {subResult.errors.length > 0 ? `, błędy: ${subResult.errors.length}` : ""}
          </span>
        )}
      </div>

      {summary && summary.errors.length > 0 && (
        <p className="text-[11px] text-amber-300/80 leading-relaxed">
          {summary.errors.map((e) => e.message).join("; ")}
        </p>
      )}

      {syncErrorMessage && (
        <p className="text-[11px] text-red-300/80 leading-relaxed font-mono break-words">
          {syncErrorMessage.replace(/^\d+:\s*/, "")}
        </p>
      )}

      {subErrorMessage && (
        <p className="text-[11px] text-red-300/80 leading-relaxed font-mono break-words">
          {subErrorMessage.replace(/^\d+:\s*/, "")}
        </p>
      )}
    </div>
  );
}

/**
 * Super_admin-only maintenance: re-subscribe EVERY connected user's Facebook Pages
 * to the Meta webhook field union, healing pages whose leadgen subscription was
 * clobbered by a messaging/IG subscribe (VP-137). The backend runs the batch in the
 * background and returns immediately, so this just kicks it off. Idempotent — safe
 * to re-run. Renders nothing for non-admins. Regular users self-heal via "Synchronizuj leady".
 */
function MetaResubscribeAllPanel() {
  const { user } = useAuth();
  const role = (user as { role?: string } | null)?.role;

  const mutation = useMutation<{ started: boolean; usersTotal: number }>({
    mutationFn: () => apiRequest("POST", "/api/admin/meta/resubscribe-all-pages"),
  });

  if (role !== "super_admin") return null;

  return (
    <div className="mt-3 rounded-lg border border-amber-400/30 bg-amber-400/[0.04] p-3 space-y-2">
      <div className="flex items-center gap-2 text-xs font-medium text-amber-300">
        <Lock className="w-3.5 h-3.5" /> Admin · Webhooki leadgen
      </div>
      <p className="text-xs text-white/50">
        Ponownie subskrybuje strony FB wszystkich użytkowników do webhooka leadgen (naprawa po
        VP-137). Idempotentne — można uruchomić wielokrotnie.
      </p>
      <button
        type="button"
        disabled={mutation.isPending}
        onClick={() => mutation.mutate()}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-500/90 hover:bg-amber-500 disabled:opacity-60 text-black text-xs font-semibold transition-colors"
      >
        {mutation.isPending ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Zap className="w-3.5 h-3.5" />
        )}
        Re-subskrybuj wszystkie strony
      </button>
      {mutation.isSuccess && mutation.data && (
        <p className="text-xs text-emerald-300">
          Uruchomiono w tle dla {mutation.data.usersTotal} użytkowników — wynik pojawi się w logach
          (CloudWatch).
        </p>
      )}
      {mutation.isError && (
        <p className="text-xs text-red-400">
          Błąd: {(mutation.error as Error)?.message ?? "nieznany"}
        </p>
      )}
    </div>
  );
}

/**
 * Slack settings: user-level preferences panel (always shown), plus an agency-level
 * tab for agency_owner / super_admin so they can connect a separate workspace and
 * forward client events to it. Acting on behalf of a client (forUserId set) hides
 * the agency tab — preferences then belong to the client being viewed.
 */
function SlackSettingsSection({ forUserId }: { forUserId: string | null }) {
  const { user } = useAuth();
  const role = (user as { role?: string } | null)?.role;
  const isAgencyOwner = role === "agency_owner" || role === "super_admin";
  const showAgencyTab = isAgencyOwner && !forUserId;

  const [tab, setTab] = useState<"user" | "agency">("user");

  const agencyClientsQuery = useQuery<{ clients: { userId: string | null; name: string }[] }>({
    queryKey: ["/api/agency/clients", "slack-prefs"],
    queryFn: () => apiRequest("GET", "/api/agency/clients"),
    enabled: showAgencyTab && tab === "agency",
    staleTime: 60_000,
  });

  const agencyClients: AgencyClientOption[] = (agencyClientsQuery.data?.clients ?? [])
    .filter((c) => !!c.userId)
    .map((c) => ({ userId: c.userId as string, label: c.name }));

  return (
    <div className="space-y-3">
      {showAgencyTab && (
        <div className="flex gap-1 border-b border-white/[0.08]">
          <button
            type="button"
            onClick={() => setTab("user")}
            className={`px-3 py-1.5 text-xs font-medium transition-colors border-b-2 -mb-px ${
              tab === "user"
                ? "text-white border-emerald-400"
                : "text-white/50 border-transparent hover:text-white/80"
            }`}
          >
            Mój Slack
          </button>
          <button
            type="button"
            onClick={() => setTab("agency")}
            className={`px-3 py-1.5 text-xs font-medium transition-colors border-b-2 -mb-px ${
              tab === "agency"
                ? "text-white border-emerald-400"
                : "text-white/50 border-transparent hover:text-white/80"
            }`}
          >
            Slack agencji (wszyscy klienci)
          </button>
        </div>
      )}

      {tab === "user" || !showAgencyTab ? (
        <SlackNotificationsPanel scope="user" />
      ) : (
        <SlackNotificationsPanel scope="agency" agencyClients={agencyClients} />
      )}
    </div>
  );
}

function CategorySection({
  catDef,
  integrations,
  statuses,
  usage,
  onOpenCapi,
  hasPerformance,
  planTier,
  onUpsell,
  openIntegrationId,
  forUserId,
}: {
  catDef: (typeof CATEGORIES)[0];
  integrations: Integration[];
  statuses: Record<string, { connected: boolean; loading: boolean; detail?: string }>;
  usage?: Record<string, PlatformUsage>;
  onOpenCapi?: () => void;
  hasPerformance: boolean;
  planTier: string | null;
  onUpsell: (integration: Integration) => void;
  openIntegrationId?: string | null;
  forUserId?: string | null;
}) {
  const [open, setOpen] = useState(true);
  const { t } = useTranslation("integrationsHub");
  const c = COLOR_MAP[catDef.color] || COLOR_MAP.red;
  const connectedCount = integrations.filter((i) => statuses[i.id]?.connected).length;

  return (
    <div className="mb-6">
      <button
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center gap-dense p-4 rounded-xl ${c.bg} border ${c.border} hover:brightness-110 transition-all text-left`}
      >
        <div className={`p-2 rounded-lg ${c.bg} ${c.text}`}>
          <catDef.icon className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="text-base font-semibold text-white">{t(catDef.label)}</div>
          <div className="text-xs text-white/50">
            {connectedCount > 0 && (
              <span className="text-emerald-400">
                {t("category.connected", { count: connectedCount })}{" "}
              </span>
            )}
            {t("category.integrations", { count: integrations.length })}
          </div>
        </div>
        {open ? (
          <ChevronUp className="w-5 h-5 text-white/40" />
        ) : (
          <ChevronDown className="w-5 h-5 text-white/40" />
        )}
      </button>
      {open && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-dense mt-3">
          {integrations.map((i) => {
            const locked = isIntegrationLocked(i, hasPerformance, planTier);
            return (
              <IntegrationCard
                key={i.id}
                integration={i}
                status={statuses[i.id]}
                planUsage={usage}
                onOpenCapi={i.id === "capi" ? onOpenCapi : undefined}
                locked={locked}
                onLockClick={locked ? () => onUpsell(i) : undefined}
                initialOpen={openIntegrationId === i.id}
                forUserId={scopeForIntegration(i.id, forUserId)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// Main Page
// ═══════════════════════════════════════════════════════

export default function IntegrationsHubPage() {
  const { user } = useAuth();
  const { t } = useTranslation("integrationsHub");
  const [, setLocation] = useLocation();
  const subscriptionPlanKey = user?.subscriptionPlanKey as string | undefined;
  const planVertical = getDefaultFilterVertical(subscriptionPlanKey);
  const planTier = getPlanTier(subscriptionPlanKey);
  // VP-116: flaga rozwiązana serwerowo (/api/auth/me) — z dziedziczeniem planu po
  // właścicielu agencji. Parsowanie `subscriptionPlanKey` po stronie frontu dawało
  // pracownikowi agencji fail-open przy backendzie odrzucającym każde żądanie.
  const planHasPerformance =
    (user as { hasPerformance?: boolean } | null)?.hasPerformance !== false;
  // VP-116: brak Performance w PLANIE — reklamy są wtedy upsellem, nie zakładką.
  const organicOnly = !planHasPerformance;
  // Shopify na go-live jest widoczne tylko dla super_admin / whitelisty testerów
  // (VP-223). Reszta userów nie widzi karty i nie odpytuje jej statusu.
  const canSeeShopify = canAccessShopifyIntegration(user);
  // Google Ads: aplikacja nie przeszła weryfikacji Google OAuth, więc każde
  // podłączenie kończy się 403 access_denied — integracja znika z UI (VP-192).
  const canSeeGoogleAds = canAccessGoogleAdsIntegration(user);
  // YouTube + LinkedIn (Content Publish) widoczne tylko dla super_admin —
  // brak oficjalnego zatwierdzenia OAuth Google/LinkedIn dla reszty userów.
  const isSuperAdmin = (user as { role?: string } | undefined)?.role === "super_admin";

  // Read URL params: ?filter=<use-case> pre-selects filter, ?open=<integration-id> auto-expands + scrolls,
  // ?settings=1 auto-opens the analytics-settings modal so the dashboard cog
  // can deep-link here instead of mounting a second copy of the modal.
  const urlParams =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();
  const filterParam = urlParams.get("filter");
  const openParam = urlParams.get("open");
  const settingsParam = urlParams.get("settings");
  const [analyticsSettingsOpen, setAnalyticsSettingsOpen] = useState(settingsParam === "1");

  // VP-300: `?context=organic` w linku (Analityka, Social Inbox, ikona integracji w sidebarze
  // w trybie Organic) zawęża hub do publikacji i analityki treści. Zamrożone w stanie, bo
  // `switchScope` przepisuje URL — inaczej przełącznik znikałby sam sobie spod palca.
  const [entryScope] = useState(() =>
    resolveIntegrationScope({ planHasPerformance, contextParam: urlParams.get("context") })
  );
  const canToggleScope = entryScope.canToggle;
  const [showPerformance, setShowPerformance] = useState(entryScope.showPerformance);

  // Plan ma weto nad zakresem widoku: gdyby `/api/auth/me` doniosło w trakcie sesji, że
  // user jednak nie ma Performance, hub nie może zostać na pełnej liście — renderowałby
  // karty, których backend nie obsłuży (403), obok zachęty do dokupienia planu.
  useEffect(() => {
    if (!planHasPerformance) setShowPerformance(false);
  }, [planHasPerformance]);

  const isValidUseCase = (v: string | null): v is UseCase =>
    v === "all" || v === "ecommerce" || v === "call_funnel" || v === "saas";

  // VP-116: w widoku organicznym filtr typu biznesu nie ma sensu (dotyczy integracji
  // Performance), a panel z chipami jest ukryty — więc `?filter=ecommerce` z linku
  // odfiltrowałby własne karty BEZ możliwości cofnięcia. Wyłączamy mechanizm, nie tylko UI.
  const defaultFilter: UseCase = !entryScope.showPerformance
    ? "all"
    : isValidUseCase(filterParam)
      ? filterParam
      : (planVertical && PLAN_DEFAULT_FILTER[planVertical]) || "all";

  const [useCase, setUseCase] = useState<UseCase>(defaultFilter);
  // Przełączenie na widok organiczny chowa chipy filtra, więc aktywny filtr
  // Performance zostałby zamrożony i wyciął karty publikacyjne bez odwrotu.
  // `?context` idzie za wyborem, żeby odświeżenie strony ani „wstecz" nie cofały decyzji.
  const switchScope = (nextShowPerformance: boolean) => {
    setShowPerformance(nextShowPerformance);
    if (!nextShowPerformance) setUseCase("all");
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (nextShowPerformance) url.searchParams.delete("context");
    else url.searchParams.set("context", ORGANIC_CONTEXT_PARAM);
    window.history.replaceState({}, "", url.toString());
  };
  const [search, setSearch] = useState("");
  const [showCapi, setShowCapi] = useState(false);
  const [upsellIntegration, setUpsellIntegration] = useState<Integration | null>(null);
  const [upsellOpen, setUpsellOpen] = useState(false);
  const openUpsell = (integration: Integration) => {
    setUpsellIntegration(integration);
    setUpsellOpen(true);
  };
  const [performancePaywallOpen, setPerformancePaywallOpen] = useState(false);

  // Agency context: when an agency user has a client picked in the global
  // store, we display & manage THAT client's integrations instead of the
  // agency user's own. Without this, an agency owner could only ever connect
  // their own platforms — no way to set up integrations for their clients.
  const {
    clients,
    isLoading: clientsLoading,
    isAgencyUser,
    selectedClientId,
    selectedClient,
    selectedClientUserId,
    setSelectedClient,
  } = useAgencyClients();
  // Read userId from persisted store (selectedClientUserId) instead of selectedClient.userId.
  // selectedClient resolves only after /api/my-clients responds, so using it causes a flicker:
  // first render fetches the agency owner's data, then refetches with the real client.
  const forUserId = isAgencyUser ? (selectedClientUserId ?? null) : null;
  const capiScope = resolveCapiClientScope({
    isAgencyUser,
    selectedClientId,
    selectedClient,
    isLoading: clientsLoading,
  });
  const statuses = useIntegrationStatuses(
    forUserId,
    canSeeShopify,
    canSeeGoogleAds,
    showPerformance
  );
  const planUsageQuery = usePlanUsage();
  const planUsage = planUsageQuery.data?.usage;

  // Auto-scroll to integration specified by ?open=<id> once card is rendered.
  useEffect(() => {
    if (!openParam) return;
    const timerId = setTimeout(() => {
      const el = document.getElementById(openParam);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 400);
    return () => clearTimeout(timerId);
  }, [openParam]);

  // Karty widoczne dla tego usera (przed filtrem use-case/search) — gating dostępu.
  const visibleIntegrations = INTEGRATIONS.filter((i) => {
    if (i.id === "shopify" && !canSeeShopify) return false;
    if (i.id === "google_ads" && !canSeeGoogleAds) return false;
    if ((i.id === "youtube" || i.id === "linkedin") && !isSuperAdmin) return false;
    // VP-116: user z samym Organikiem nie widzi integracji Performance. Puste kategorie
    // odpadają same niżej, więc znikają też nagłówki „CRM & Telefonia" itd.
    if (i.plan === "performance" && !showPerformance) return false;
    return true;
  }).map((i) =>
    // VP-116 (ADR-0006 D2): Meta to jedno OAuth — w widoku organicznym to kanał
    // publikacji, nie reklam. Sama karta i połączenie zostają te same, zmienia się opis.
    !showPerformance && i.id === "meta"
      ? { ...i, name: t("integrations.meta.nameOrganic"), desc: "integrations.meta.descOrganic" }
      : i
  );

  const filtered = visibleIntegrations.filter((i) => {
    if (useCase !== "all" && !i.useCases.includes(useCase)) return false;
    if (search) {
      const q = search.toLowerCase();
      return i.name.toLowerCase().includes(q) || i.desc.toLowerCase().includes(q);
    }
    return true;
  });

  const grouped = CATEGORIES.map((cat) => ({
    ...cat,
    // Po odfiltrowaniu reklam w kategorii `ads` zostaje samo publikowanie treści —
    // nagłówek „Platformy reklamowe" wprowadzałby w błąd.
    label: !showPerformance && cat.id === "ads" ? "categories.adsOrganic" : cat.label,
    items: filtered.filter((i) => i.category === cat.id),
  })).filter((g) => g.items.length > 0);

  const totalConnected = visibleIntegrations.filter((i) => statuses[i.id]?.connected).length;
  // VP-116: mianownik liczony po WIDOCZNYCH kartach. `statuses` zawiera wpisy dla
  // wszystkich platform, więc Organicowi wychodziło „0/20 połączonych · 3 dostępne".
  const totalWithStatus = visibleIntegrations.filter((i) => i.id in statuses).length;

  // If CAPI page is open, render it full-screen
  if (showCapi) {
    return (
      <div className="fixed inset-0 overflow-y-auto bg-[#030407] text-white/90">
        <div className="max-w-5xl mx-auto px-4 py-6">
          {capiScope.state === "ready" ? (
            <CapiConfigPage onBack={() => setShowCapi(false)} forUserId={capiScope.forUserId} />
          ) : capiScope.state === "loading" ? (
            <div className="space-y-6">
              <div className="space-y-2">
                <Skeleton className="h-8 w-64" />
                <Skeleton className="h-4 w-80 max-w-full" />
              </div>
              <KpiGridSkeleton count={4} className="grid grid-cols-2 lg:grid-cols-4 gap-3" />
              <TableSkeleton rows={5} columns={4} />
            </div>
          ) : (
            <div className="mx-auto mt-20 max-w-lg rounded-xl border border-red-500/20 bg-red-500/[0.04] p-6">
              <h1 className="text-lg font-semibold text-white">
                {t("agency.capiScopeUnavailableTitle")}
              </h1>
              <p className="mt-2 text-sm text-white/60">
                {t("agency.capiScopeUnavailableDescription")}
              </p>
              <button
                type="button"
                onClick={() => setShowCapi(false)}
                className="mt-5 rounded-md border border-white/10 px-4 py-2 text-sm text-white transition-colors hover:bg-white/[0.06]"
              >
                {t("agency.capiScopeBack")}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 overflow-y-auto bg-[#030407] text-white/90">
      {/* VP-116: wspólny PaywallModal zamiast lokalnego popupu z zahardkodowanymi tekstami.
          Nie zerujemy `upsellIntegration` przy zamykaniu — Dialog animuje wyjście ~200 ms,
          a wyczyszczony tytuł podmieniłby się w tym czasie na generyczny. */}
      <PaywallModal
        open={upsellOpen}
        onClose={() => setUpsellOpen(false)}
        title={upsellIntegration ? t("upsell.lockedTitle", { name: upsellIntegration.name }) : ""}
        description={
          upsellIntegration
            ? t(
                resolveLockState(upsellIntegration, planHasPerformance, planTier).reason === "tier"
                  ? "upsell.tierBody"
                  : "upsell.performanceBody"
              )
            : ""
        }
      />
      {/* Header */}
      <div className="border-b border-white/[0.08] bg-[#030407]/95 backdrop-blur-md sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-balanced">
          <button
            type="button"
            // Zawsze dashboard w shellu, NIE history.back(): po claimie sklepu Shopify
            // poprzednim wpisem historii jest /integracje/shopify/claim?token=… ze
            // zużytym tokenem — „wstecz" lądowało na ekranie błędu podpięcia.
            onClick={() => setLocation(DASHBOARD_PATH)}
            className="p-2 rounded-lg hover:bg-[#0a0c10] text-white/60 hover:text-white transition-colors"
            aria-label="Wróć"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-white">{t("header.title")}</h1>
            <p className="text-sm text-white/50">
              {t("header.subtitle", {
                connected: totalConnected,
                total: totalWithStatus,
                available: visibleIntegrations.length,
              })}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Agency client switcher — only shown for agency users with at least
            one client. Lets the agency owner/employee pick whose integrations
            they're configuring; defaults to their own (no client selected). */}
        {isAgencyUser && clients.length > 0 && (
          <div className="rounded-xl border border-purple-500/20 bg-purple-500/[0.04] p-4 space-y-2">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-purple-400" />
              <span className="text-sm font-semibold text-white">
                {t("agency.clientPickerTitle", "Konfigurujesz integracje dla:")}
              </span>
            </div>
            <select
              value={selectedClient?.id ?? ""}
              onChange={(e) => {
                const id = e.target.value;
                if (!id) {
                  setSelectedClient(null);
                  return;
                }
                const c = clients.find((x) => x.id === id);
                if (c) setSelectedClient({ id: c.id, userId: c.userId, name: c.name });
              }}
              className="w-full h-9 px-3 text-sm bg-[#030407] border border-white/[0.1] rounded-md text-white"
            >
              <option value="">
                {t("agency.selfOption", "Moje integracje (właściciel agencji)")}
              </option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-white/40">
              {t(
                "agency.clientPickerHint",
                "Status połączenia, połącz/odłącz dotyczy wybranego konta. Zmień klienta żeby zarządzać jego integracjami."
              )}
            </p>
          </div>
        )}

        {/* VP-300: zakres huba — tylko dla kogoś, kto PRZYSZEDŁ z Organica i ma obie części
            produktu. Kto wszedł z części reklamowej, nie dostaje możliwości zawężenia sobie
            listy (tak samo jak w sidebarze), a dla Organica reklamy są upsellem niżej.
            Przełącznik jest lokalny: nie przestawia trybu sidebara, zmienia tylko tę stronę. */}
        {canToggleScope && (
          <div className="flex flex-col gap-dense rounded-xl border border-white/[0.08] bg-white/[0.03] p-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-sm font-semibold text-white">{t("scope.title")}</div>
              <p className="text-xs text-white/50">
                {t(showPerformance ? "scope.adsHint" : "scope.organicHint")}
              </p>
            </div>
            <div className="flex rounded-lg border border-white/[0.08] bg-white/[0.04] p-0.5">
              {SCOPE_TABS.map((tab) => {
                const active = showPerformance === tab.showPerformance;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    data-testid={`integrations-scope-${tab.id}`}
                    onClick={() => switchScope(tab.showPerformance)}
                    aria-pressed={active}
                    className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                      active
                        ? "bg-red-500/15 text-white"
                        : "text-white/45 hover:text-white/75 hover:bg-white/[0.04]"
                    }`}
                  >
                    {t(tab.label)}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Ustawienia analityki — single entry point for KPI / dashboard config.
            The modal lives here (not in the sidebar) so users have ONE place to
            both connect integrations and configure dashboard KPIs.
            VP-116: ukryte dla Organica — /api/analytics-settings zwraca mu 403,
            więc byłby to najbardziej wyeksponowany przycisk na stronie prowadzący
            wprost do błędu. */}
        {showPerformance && (
          <button
            onClick={() => setAnalyticsSettingsOpen(true)}
            className="w-full flex items-center gap-3 p-4 rounded-xl bg-gradient-to-r from-[#ef3e29]/10 to-transparent border border-[#ef3e29]/20 hover:border-[#ef3e29]/40 transition-all group text-left"
          >
            <div className="p-2.5 rounded-lg bg-[#ef3e29]/15 text-[#ef3e29] group-hover:bg-[#ef3e29]/25 transition-colors">
              <SlidersHorizontal className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="text-base font-semibold text-white">
                {t("analyticsSettings.title", "Ustawienia analityki")}
              </div>
              <div className="text-xs text-white/50">
                {t(
                  "analyticsSettings.desc",
                  "Tryb dashboardu, konta reklamowe, cele KPI, monitoring"
                )}
              </div>
            </div>
            <ExternalLink className="w-4 h-4 text-white/30 group-hover:text-white/60" />
          </button>
        )}

        {/* Use-case filter — VP-116: filtr typu biznesu dotyczy wyłącznie integracji
            Performance, więc przy samym Organiku nie ma czego filtrować.
            Warunkowy render, nie klasa `hidden` — spójnie z resztą pliku. */}
        {showPerformance && (
          <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Layers className="w-5 h-5 text-red-400" />
              <h2 className="text-base font-semibold text-white">{t("filter.title")}</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-dense">
              {USE_CASES.map((uc) => {
                const Icon = uc.icon;
                const active = useCase === uc.id;
                return (
                  <button
                    key={uc.id}
                    onClick={() => setUseCase(uc.id)}
                    className={`flex flex-col items-center gap-2 p-3 rounded-xl border transition-all text-center ${
                      active
                        ? "bg-red-500/15 border-red-500/40 text-white shadow-lg shadow-red-500/10"
                        : "bg-white/[0.03] border-white/[0.08] text-white/60 hover:bg-white/[0.06] hover:text-white/80"
                    }`}
                  >
                    <Icon className="w-6 h-6" />
                    <span className="text-xs font-medium">{t(uc.label)}</span>
                    <span className="text-[10px] text-white/40">{t(uc.desc)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <input
            type="text"
            placeholder={t("search.placeholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-red-500/40 focus:ring-1 focus:ring-red-500/20"
          />
        </div>

        {/* Categories — kłódka zależy od PLANU, nie od zakresu widoku (VP-300):
            w widoku organicznym karty Performance i tak nie są renderowane. */}
        {grouped.map((g) => (
          <CategorySection
            key={g.id}
            catDef={g}
            integrations={g.items}
            statuses={statuses}
            usage={planUsage}
            onOpenCapi={() => setShowCapi(true)}
            hasPerformance={planHasPerformance}
            planTier={planTier}
            onUpsell={openUpsell}
            openIntegrationId={openParam}
            forUserId={forUserId}
          />
        ))}

        {/* VP-116: zamiast kilkunastu wyszarzonych kart Performance — jedna zachęta.
            Pod kategoriami, żeby nie przesłaniać tego, co user faktycznie ma. */}
        {organicOnly && (
          <button
            type="button"
            onClick={() => setPerformancePaywallOpen(true)}
            className="w-full text-left bg-white/[0.03] border border-white/[0.08] rounded-xl p-5 hover:bg-white/[0.06] hover:border-white/[0.14] transition-all group"
          >
            <div className="flex items-start gap-balanced">
              <div className="p-2.5 bg-red-500/10 border border-red-500/20 rounded-xl shrink-0">
                <Lock className="w-5 h-5 text-red-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-white mb-1">
                  {t("performanceUpsell.title")}
                </h3>
                <p className="text-sm text-white/50">{t("performanceUpsell.body")}</p>
              </div>
              <ExternalLink className="w-4 h-4 text-white/30 group-hover:text-white/60 shrink-0 mt-1" />
            </div>
          </button>
        )}

        {grouped.length === 0 && (
          <div className="text-center py-16">
            <Search className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <p className="text-white/50">{t("search.noResults")}</p>
          </div>
        )}
      </div>

      {/* Analytics-settings modal — opened via the "Ustawienia analityki"
          button above. Single entry point for KPI / dashboard configuration. */}
      <PaywallModal
        open={performancePaywallOpen}
        onClose={() => setPerformancePaywallOpen(false)}
        title={t("performanceUpsell.title")}
        description={t("performanceUpsell.body")}
      />

      <AnalyticsSettingsModal
        open={analyticsSettingsOpen}
        onOpenChange={setAnalyticsSettingsOpen}
      />
    </div>
  );
}
